import api from "../config/api";
import type { ApiResponse, PaginatedResponse } from "../types";

// Commission Types
export interface Commission {
  _id: string;
  employee: {
    _id: string;
    name: string;
    employeeId: string;
    role: string;
    commissionRate?: number;
  };
  month: number;
  year: number;
  serviceCommissions: Array<{
    _id: string;
    serviceRecord: {
      _id: string;
      recordId: string;
      startTime: string;
      endTime: string;
    };
    course: {
      _id: string;
      name: string;
    };
    serviceDate: string;
    hoursWorked: number;
    isOvertime: boolean;
    baseAmount: number;
    commissionRate: number;
    commissionAmount: number;
    currency: 'LAK' | 'THB' | 'USD';
  }>;
  referralCommissions: Array<{
    _id: string;
    customer: {
      _id: string;
      name: string;
      customerId: string;
    };
    receipt: {
      _id: string;
      receiptNumber: string;
    };
    referralDate: string;
    saleAmount: number;
    commissionRate: number;
    commissionAmount: number;
    currency: 'LAK' | 'THB' | 'USD';
  }>;
  overtimeCommissions: Array<{
    _id: string;
    serviceRecord: {
      _id: string;
      recordId: string;
    };
    overtimeHours: number;
    overtimeRate: number;
    baseHourlyRate: number;
    overtimeAmount: number;
    currency: 'LAK' | 'THB' | 'USD';
  }>;
  totalServiceCommission: {
    LAK: number;
    THB: number;
    USD: number;
  };
  totalReferralCommission: {
    LAK: number;
    THB: number;
    USD: number;
  };
  totalOvertimeCommission: {
    LAK: number;
    THB: number;
    USD: number;
  };
  grandTotal: {
    LAK: number;
    THB: number;
    USD: number;
  };
  isPaid: boolean;
  paidAt?: string;
  paidBy?: {
    _id: string;
    name: string;
    employeeId: string;
  };
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CommissionSummary {
  month: number;
  year: number;
  summary: {
    totalEmployees: number;
    totalPaid: number;
    totalUnpaid: number;
    totalAmount: {
      LAK: number;
      THB: number;
      USD: number;
    };
    paidAmount: {
      LAK: number;
      THB: number;
      USD: number;
    };
    unpaidAmount: {
      LAK: number;
      THB: number;
      USD: number;
    };
  };
  commissions: Commission[];
}

// Request Interfaces
export interface GetCommissionsParams {
  employee?: string;
  month?: number;
  year?: number;
  isPaid?: boolean;
  page?: number;
  limit?: number;
}

export interface CalculateCommissionRequest {
  employee: string;
  month: number;
  year: number;
}

export interface PayCommissionRequest {
  notes?: string;
}

// Commission Service
export const commissionService = {
  // ดึงรายการค่าคอมมิชชัน
  getCommissions: async (params: GetCommissionsParams = {}): Promise<PaginatedResponse<Commission>> => {
    const response = await api.get('/commissions', { params });
    return response.data;
  },

  // ดึงข้อมูลค่าคอมมิชชันตาม ID
  getCommission: async (id: string): Promise<ApiResponse<Commission>> => {
    const response = await api.get(`/commissions/${id}`);
    return response.data;
  },

  // คำนวณค่าคอมมิชชันประจำเดือน
  calculateMonthlyCommission: async (data: CalculateCommissionRequest): Promise<ApiResponse<Commission>> => {

    console.log("c data--->", data)

    const response = await api.post('/commissions/calculate', data);
    return response.data;
  },

  // จ่ายค่าคอมมิชชัน
  payCommission: async (id: string, data: PayCommissionRequest = {}): Promise<ApiResponse<Commission>> => {
    const response = await api.put(`/commissions/${id}/pay`, data);
    return response.data;
  },

  // ดึงสรุปค่าคอมมิชชันรายเดือน
  getCommissionSummary: async (month: number, year: number): Promise<ApiResponse<CommissionSummary>> => {
    const response = await api.get(`/commissions/summary/${month}/${year}`);
    return response.data;
  },

  // Helper methods สำหรับใช้งานเฉพาะ

  // ดึงค่าคอมมิชชันที่ยังไม่จ่าย
  getUnpaidCommissions: async (params: GetCommissionsParams = {}): Promise<PaginatedResponse<Commission>> => {
    return commissionService.getCommissions({ ...params, isPaid: false });
  },

  // ดึงค่าคอมมิชชันที่จ่ายแล้ว
  getPaidCommissions: async (params: GetCommissionsParams = {}): Promise<PaginatedResponse<Commission>> => {
    return commissionService.getCommissions({ ...params, isPaid: true });
  },

  // ดึงค่าคอมมิชชันตามพนักงาน
  getCommissionsByEmployee: async (employeeId: string, params: GetCommissionsParams = {}): Promise<PaginatedResponse<Commission>> => {
    return commissionService.getCommissions({ ...params, employee: employeeId });
  },

  // ดึงค่าคอมมิชชันตามเดือน/ปี
  getCommissionsByMonthYear: async (month: number, year: number, params: GetCommissionsParams = {}): Promise<PaginatedResponse<Commission>> => {
    return commissionService.getCommissions({ ...params, month, year });
  },

  // คำนวณค่าคอมมิชชันหลายຄົນพร้อมกัน
  calculateMultipleCommissions: async (employees: Array<{ employee: string; month: number; year: number }>): Promise<ApiResponse<Commission[]>> => {
    const results = await Promise.allSettled(
      employees.map(emp => commissionService.calculateMonthlyCommission(emp))
    );
    
    const successful = results
      .filter((result): result is PromiseFulfilledResult<ApiResponse<Commission>> => result.status === 'fulfilled')
      .map(result => result.value.data);
    
    return {
      success: true,
      data: successful,
      message: `คำนวณสำเร็จ ${successful.length} จาก ${employees.length} ຄົນ`
    };
  },

  // จ่ายค่าคอมมิชชันหลายຄົນพร้อมกัน
  payMultipleCommissions: async (commissionIds: string[], notes?: string): Promise<ApiResponse<Commission[]>> => {
    const results = await Promise.allSettled(
      commissionIds.map(id => commissionService.payCommission(id, { notes }))
    );
    
    const successful = results
      .filter((result): result is PromiseFulfilledResult<ApiResponse<Commission>> => result.status === 'fulfilled')
      .map(result => result.value.data);
    
    return {
      success: true,
      data: successful,
      message: `จ่ายสำเร็จ ${successful.length} จาก ${commissionIds.length} รายการ`
    };
  },

  // ดึงค่าคอมมิชชันของเดือนปัจจุบัน
  getCurrentMonthCommissions: async (params: GetCommissionsParams = {}): Promise<PaginatedResponse<Commission>> => {
    const now = new Date();
    return commissionService.getCommissions({
      ...params,
      month: now.getMonth() + 1,
      year: now.getFullYear()
    });
  },

  // ดึงค่าคอมมิชชันของเดือนที่แล้ว
  getLastMonthCommissions: async (params: GetCommissionsParams = {}): Promise<PaginatedResponse<Commission>> => {
    const now = new Date();
    const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1);
    return commissionService.getCommissions({
      ...params,
      month: lastMonth.getMonth() + 1,
      year: lastMonth.getFullYear()
    });
  },

  // คำนวณสรุปยอดລວມทั้งหมด
  calculateTotalCommissions: (commissions: Commission[]) => {
    return commissions.reduce((total, commission) => ({
      LAK: total.LAK + commission.grandTotal.LAK,
      THB: total.THB + commission.grandTotal.THB,
      USD: total.USD + commission.grandTotal.USD,
    }), { LAK: 0, THB: 0, USD: 0 });
  },

  // แปลงเดือนเป็นชื่อภาษาไทย
  getMonthNameTH: (month: number): string => {
    const months = [
      'ມັງກອນ', 'ກຸມພາ', 'ມີນາ', 'ເມສາ', 'ພຶດສະພາ', 'ມິຖຸນາ',
      'ກໍລະກົດ', 'ສິງຫາ', 'ກັນຍາ', 'ຕຸລາ', 'ພະຈິກ', 'ທັນວາ'
    ];
    return months[month - 1] || '';
  },

  // ตรวจสอบว่าสามารถคำนวณค่าคอมมิชชันได้หรือไม่
  canCalculateCommission: (month: number, year: number): boolean => {
    const now = new Date();
    const targetDate = new Date(year, month - 1);
    return targetDate <= now;
  }
};